import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { classes, style } from "typestyle";
import { useShellHost } from "@/shell-handle/ShellHostProvider";
import type { MfeErrorState } from "@/shell-handle/mfeError";
import { ErrorPage } from "@/pages/ErrorPage/ErrorPage";
import { Button, Styles } from "@/shared/coralreef";
import { DESKTOP_DEV, retryMfeLoad } from "@/single-spa-config/apps";

function DevReloadButton({ title }: { title: string }) {
  return (
    <div data-testid="mfe-error-reload" title={title}>
      <Button
        content="Reload (keep session)"
        appearance="hollow"
        onClick={() => window.location.reload()}
      />
    </div>
  );
}

function MfeLoadError({ error }: { error: MfeErrorState }) {
  const { t } = useTranslation();
  const [retrying, setRetrying] = useState(false);
  const slug = error.appName.replace(/^@[^/]+\//, "");

  const retry = () => {
    setRetrying(true);
    void retryMfeLoad().finally(() => setRetrying(false));
  };

  return (
    <div
      role="alert"
      data-testid={`mfe-load-error-${slug}`}
      className={classes(
        "absolute inset-0 z-[999] flex flex-col items-center justify-center gap-4 p-6 text-center",
        style({
          background: Styles.Colours.darkestBlue,
          color: Styles.Colours.textLight,
        }),
      )}
    >
      <h2 className="text-xl font-semibold">
        {t("This page could not be loaded")}
      </h2>
      <p>{t("Check your network connection and try again.")}</p>
      <p className="text-sm opacity-70">{slug}</p>
      <Button
        data-testid={`mfe-load-retry-${slug}`}
        content={t("Retry")}
        appearance="hollow"
        loading={retrying}
        onClick={retry}
      />
      {DESKTOP_DEV && (
        <DevReloadButton title={`${error.appName}: ${error.message}`} />
      )}
    </div>
  );
}

export function MfeErrorOverlay() {
  const host = useShellHost();
  const [err, setErr] = useState<MfeErrorState | null>(
    host.mfeError.state.get(),
  );

  useEffect(() => host.mfeError.state.subscribe(setErr), [host]);

  if (!err) return null;
  if (err.loadFailed) return <MfeLoadError error={err} />;
  return (
    <div
      data-testid="mfe-error-overlay"
      data-mfe-app={err.appName}
      data-mfe-message={err.message}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999,
        background: Styles.Colours.darkestBlue,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        overflowY: "auto",
      }}
    >
      <ErrorPage />
      {DESKTOP_DEV && (
        // ErrorPage's Refresh signs out; in live-mfe dev the usual cause is
        // the MFE dev server mid-restart, and a plain reload keeps the session.
        <DevReloadButton title={`${err.appName}: ${err.message}`} />
      )}
    </div>
  );
}
